import { Avatar, Button, Chip, ListItem, ListItemAvatar, ListItemButton, ListItemText } from "@mui/material";
import { DRAWER_MAX_SELECTION_COUNT } from "app/appConstants";
import { AppImages } from "app/appImages";
import { GenericAbortSignal } from "axios";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { GroupSelectorResponse, GroupSelectorResponsePaginatedList, GroupType, GroupUsages, UserSelectionEntityType, UserSelectionEntry } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListChildComponentProps } from "react-window";
import { GenericListDrawer, GenericListFilter, GenericListItemsType } from "views/common/GenericListDrawer";

////////////////////////////////////////////////////////////////////////////////

type ItemData = GenericListItemsType<GroupSelectorResponse>;

////////////////////////////////////////////////////////////////////////////////

export type GroupSelectorDrawerResult = {
    groupId: string;
    name: string;
    groupType: GroupType;
    thumbnailId?: string | null;
};

interface IPros {
    title?: string;
    subtitle?: string;
    multi?: boolean;
    groupTypes?: GroupType[];
    groupUsages?: GroupUsages;
    emptyDataMessage?: string;
    emptyFilterMessage?: string;
    onClose: (group: GroupSelectorDrawerResult[] | undefined) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const GroupSelectorDrawer: React.FC<IPros> = (props) => {
    const api = useCoreApi();
    const session = useSession();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [saveSelection, setSaveSelection] = useState<GroupSelectorResponse[] | undefined>(undefined);
    const [selected, setSelection] = useState<GroupSelectorResponse[]>([]);

    const { title, subtitle, multi = false, groupTypes, groupUsages, emptyDataMessage, emptyFilterMessage, onClose } = props;

    ////////////////////////////////////////////////////////////////////////////////

    const handleGetData = (genericFilter: GenericListFilter, signal: GenericAbortSignal | undefined): Promise<GroupSelectorResponsePaginatedList> => {
        return new Promise((resolve, reject) => {
            api.v1.selectors.groups({
                ...genericFilter,
                ignoreSelectionHistory: genericFilter.ignoreSelectionHistory ?? false,
                groupTypes: groupTypes ?? null,
                groupUsages: groupUsages ?? null,
                exceptGroupIds: [],
                includeAllInternalExternalUserGroups: null,
            }, { signal })
                .then((response) => resolve(response.data))
                .catch(reject);
        });
    };

    /**
     * Renderiza uma linha da Lista.
     * @param props
     */
    function renderRow(props: ListChildComponentProps<ItemData>) {
        const { index, style } = props;
        const { items, onViewerClick } = props.data;

        const item = items[index];
        if (item == undefined) return <></>;

        const hasDivider = index > 0 && items[index].fromHistory === false && items[index - 1].fromHistory === true;

        return (
            <>
                <div key={index} style={style}>
                    <ListItem disablePadding disableGutters>
                        <ListItemButton disableGutters onClick={() => onViewerClick && onViewerClick(item)}
                            sx={(theme) => ({
                                marginTop: hasDivider ? "4px" : undefined,
                                paddingTop: hasDivider ? "4px" : theme.spacing(1),
                                borderTop: hasDivider ? "solid 2px" : undefined,
                                borderTopColor: hasDivider ? theme.palette.divider : undefined
                            })}
                        >
                            <ListItemAvatar sx={{ minWidth: 40 }}>
                                <Avatar
                                    src={
                                        item.thumbnailUrl == null
                                            ? AppImages.GroupThumbnail
                                            : item.thumbnailUrl
                                    }
                                    sx={(theme) => ({ width: 32, height: 32, backgroundColor: theme.palette.grey[200] })}
                                />
                            </ListItemAvatar >
                            <ListItemText
                                primary={item.name}
                                title={item.name}
                                slotProps={
                                    {
                                        primary: { noWrap: true, variant: "h6" }
                                    }
                                }
                            />
                        </ListItemButton>
                    </ListItem>
                </div>
            </>
        );
    }

    // Ao selecionar um item da lista.
    const onItemClick = (item: GroupSelectorResponse) => {
        if (!multi)
            setSaveSelection([item]);
        else {
            if (selected.find(p => p.groupId == item.groupId) == null) {
                if (selected.length == DRAWER_MAX_SELECTION_COUNT)
                    enqueueSnackbar(t("Group.GroupSelectorDrawer.SelectionLimitedMessage", { count: DRAWER_MAX_SELECTION_COUNT }), { variant: "warning" });
                else
                    setSelection([...selected, item]);
            }
            else {
                enqueueSnackbar(t("Group.GroupSelectorDrawer.AlreadySelectedMessage", { name: item.name }), { variant: "error" });
            }
        }
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    /// Salva itens selecionados e retorna.
    useEffect(() => {
        if (saveSelection != undefined) {
            var payload = {
                userId: session.user.userId,
                entries: saveSelection.filter(p => !p.fromHistory).map((v) => ({
                    entityType: UserSelectionEntityType.Group,
                    entityId: v.groupId,
                    otherInfo: ""
                } as UserSelectionEntry))
            };

            if (payload.entries.length > 0) {
                api.v1.user.saveSelectionHistory(payload)
                    .then(() => {
                        onClose(saveSelection);
                    })
                    .catch((e) => {
                        setSaveSelection(undefined);
                        MprcRequestSnackbarErrorReport(e);
                    });
            }
            else {
                onClose(saveSelection);
            }
        }
    }, [saveSelection]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <GenericListDrawer
            title={title || t("Group.GroupSelectorDrawer.Title")}
            subtitle={subtitle}
            itemHeight={40}
            onClose={() => onClose(undefined)}
            onGetData={handleGetData}
            onViewerClick={onItemClick}
            emptyDataMessage={emptyDataMessage ?? t("Group.GroupSelectorDrawer.ListEmptyMessage")}
            emptyFilterMessage={emptyFilterMessage ?? t("Group.GroupSelectorDrawer.NoRecordsFound")}
            renderRow={renderRow}
            selection="history"
            footer={
                <div style={{ width: "100%" }}>
                    {
                        multi && selected.length > 0 &&
                        <>
                            <div style={{ maxHeight: 200, overflow: "auto" }}>
                                <p><small>{t("shared:Selector.SelectedText")}:</small></p>
                                {
                                    selected.map((item, index) => (
                                        <Chip
                                            key={index}
                                            label={item.name}
                                            size="small"
                                            sx={{ mb: 0.5, mr: 0.5 }}
                                            avatar={
                                                <Avatar
                                                    src={
                                                        item.thumbnailUrl == null
                                                            ? AppImages.GroupThumbnail
                                                            : item.thumbnailUrl
                                                    }
                                                />
                                            }
                                            onDelete={() =>
                                                setSelection(selected.filter(p => p.groupId != item.groupId))
                                            }
                                        />
                                    ))
                                }
                            </div>
                            <hr />
                            <div style={{ textAlign: "center" }}>
                                <Button onClick={() => onClose(selected)} variant="contained">
                                    {t("Group.GroupSelectorDrawer.AddSelectedButtonText")}
                                </Button >
                            </div>
                        </>
                    }
                </div>
            }
        />
    );
};
